import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Plan, GetPlansParams, PlanSortField, PlanIdentifier } from '../types/plan.types';
import { getPlans, archivePlans, confirmPlans } from '../services/plans.service';
import { usePlanSearchStore } from '../stores/plan-search.store';

import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { useSnackbar } from '@/hooks/use-snackbar';

export const usePlans = () => {
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const { setIsLoading } = useLoadingProgress();
  const { setSnackbarMessage } = useSnackbar();

  // Get state from global store
  const {
    searchInputValues,
    committedSearchParams,
    currentPage,
    itemsPerPage,
    sortField: storeSortField,
    sortOrder,
    setSearchInputValues,
    setCommittedSearchParams,
    setCurrentPage,
    setItemsPerPage,
    setSortField: setStoreSortField,
    setSortOrder,
    clearSearch,
  } = usePlanSearchStore();

  // Convert store sortField to PlanSortField enum
  const sortField = useMemo<PlanSortField | undefined>(() => {
    if (!storeSortField) return PlanSortField.PLACEMENT_DATE;
    return Object.values(PlanSortField).includes(storeSortField as PlanSortField)
      ? (storeSortField as PlanSortField)
      : PlanSortField.PLACEMENT_DATE;
  }, [storeSortField]);

  // Build API query parameters from committed search params
  const apiParams: GetPlansParams = useMemo(() => {
    const { isArchive, ...restSearchParams } = committedSearchParams;

    // UI uses isArchive, API expects hasArchived
    return {
      ...(restSearchParams as GetPlansParams),
      isArchive: isArchive ? true : false,
      page: currentPage,
      perPage: itemsPerPage,
      sort: sortField,
      order: sortOrder,
    };
  }, [committedSearchParams, currentPage, itemsPerPage, sortField, sortOrder]);

  // Handle sort change
  const handleSortChange = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      // Map string field to PlanSortField enum, default to PLACEMENT_DATE if invalid
      // Backend currently only supports placementDate sorting
      const validField = Object.values(PlanSortField).includes(field as PlanSortField)
        ? (field as PlanSortField)
        : PlanSortField.PLACEMENT_DATE;
      setStoreSortField(validField);
      setSortOrder(order);
    },
    [setStoreSortField, setSortOrder]
  );

  // Fetch plans using React Query
  const {
    data: apiResponse,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['plans', apiParams],
    queryFn: () => {
      return getPlans(apiParams);
    },
    staleTime: 0, // Always consider data stale to refetch on sort change
    refetchOnMount: true, // Refetch when component mounts
    gcTime: 0, // Don't cache query results (previously cacheTime)
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new data for better UX
  });

  // Extract plans from API response
  const plans = useMemo<Plan[]>(() => {
    const plansData = apiResponse?.items || [];
    return plansData;
  }, [apiResponse]);

  // Total items from API pagination
  const totalItems = apiResponse?.pagination?.total || 0;

  // Handle search - Only called when user clicks search button
  const handleSearch = useCallback(
    (params: Record<string, unknown>, preservePage = false) => {
      setCommittedSearchParams(params);
      setSearchInputValues(params);
      // Only reset to page 1 if it's a new search, preserve page if restoring
      if (!preservePage) {
        setCurrentPage(1);
      }
      // Note: React Query will automatically refetch when apiParams change (which includes committedSearchParams and currentPage)
      // Don't call refetch() manually to avoid double API calls
    },
    [setCommittedSearchParams, setSearchInputValues, setCurrentPage]
  );

  // Handle search change - Only updates form state (save input values to store)
  const handleSearchChange = useCallback(
    (params: Record<string, unknown>) => {
      setSearchInputValues(params);
    },
    [setSearchInputValues]
  );

  // Handle clear search
  const handleClear = useCallback(() => {
    clearSearch();
    setSelectedPlans([]);
  }, [clearSearch]);

  // Note: We don't reset page automatically when committedSearchParams change
  // The handleSearch function handles resetting page for new searches
  // When restoring, preservePage flag prevents resetting page

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  // Handle items per page change
  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: number) => {
      setItemsPerPage(itemsPerPage);
      setCurrentPage(1);
    },
    [setItemsPerPage, setCurrentPage]
  );

  // Handle selection change
  const handleSelectionChange = useCallback((selectedIds: string[]) => {
    setSelectedPlans(selectedIds);
  }, []);

  // Convert plan IDs to PlanIdentifier format
  const getPlanIdentifiers = useCallback(
    (planIds: string[]): PlanIdentifier[] => {
      return planIds
        .map((id) => {
          const plan = plans.find((p) => p.id === id);
          if (!plan) return null;
          return {
            id: plan.id,
          };
        })
        .filter((item): item is PlanIdentifier => item !== null);
    },
    [plans]
  );

  // Handle archive
  const handleArchive = useCallback(
    async (planIds: string[]) => {
      if (planIds.length === 0) {
        return;
      }
      try {
        setIsLoading(true);
        const planIdentifiers = getPlanIdentifiers(planIds);

        if (planIdentifiers.length === 0) {
          setSnackbarMessage('選択されたプランが見つかりません', { type: 'error' });
          return;
        }

        const result = await archivePlans(planIdentifiers, true);

        if (result.success.length > 0) {
          setSnackbarMessage(`${result.success.length}件のプランをアーカイブしました`, {
            type: 'success',
          });
          await refetch();
          setSelectedPlans([]);
        }

        if (result.notFound.length > 0) {
          setSnackbarMessage(`${result.notFound.length}件のプランが見つかりませんでした`, {
            type: 'error',
          });
        }
      } catch (error) {
        console.error('Error archiving plans:', error);
        setSnackbarMessage('アーカイブに失敗しました', { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [refetch, setIsLoading, setSnackbarMessage, getPlanIdentifiers]
  );

  // Action handlers for filter buttons - these perform actions on selected items
  const handleConfirmed = useCallback(
    async (planIds: string[]) => {
      if (planIds.length === 0) {
        return;
      }
      try {
        setIsLoading(true);
        const planIdentifiers = getPlanIdentifiers(planIds);

        if (planIdentifiers.length === 0) {
          setSnackbarMessage('選択されたプランが見つかりません', { type: 'error' });
          return;
        }

        const result = await confirmPlans(planIdentifiers, true);

        if (result.success.length > 0) {
          setSnackbarMessage(`${result.success.length}件のプランを前日確認しました`, {
            type: 'success',
          });
          await refetch();
          setSelectedPlans([]);
        }

        if (result.notFound.length > 0) {
          setSnackbarMessage(`${result.notFound.length}件のプランが見つかりませんでした`, {
            type: 'error',
          });
        }
      } catch (error) {
        console.error('Error confirming plans:', error);
        setSnackbarMessage('前日確認に失敗しました', { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [refetch, setIsLoading, setSnackbarMessage, getPlanIdentifiers]
  );

  const handleUnconfirmed = useCallback(
    async (planIds: string[]) => {
      if (planIds.length === 0) {
        return;
      }
      try {
        setIsLoading(true);
        const planIdentifiers = getPlanIdentifiers(planIds);

        if (planIdentifiers.length === 0) {
          setSnackbarMessage('選択されたプランが見つかりません', { type: 'error' });
          return;
        }

        const result = await confirmPlans(planIdentifiers, false);

        if (result.success.length > 0) {
          setSnackbarMessage(`${result.success.length}件のプランを未確認にしました`, {
            type: 'success',
          });
          await refetch();
          setSelectedPlans([]);
        }

        if (result.notFound.length > 0) {
          setSnackbarMessage(`${result.notFound.length}件のプランが見つかりませんでした`, {
            type: 'error',
          });
        }
      } catch (error) {
        console.error('Error unconfirming plans:', error);
        setSnackbarMessage('未確認への変更に失敗しました', { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [refetch, setIsLoading, setSnackbarMessage, getPlanIdentifiers]
  );

  const handleActive = useCallback(
    async (planIds: string[]) => {
      if (planIds.length === 0) {
        return;
      }
      try {
        setIsLoading(true);
        const planIdentifiers = getPlanIdentifiers(planIds);

        if (planIdentifiers.length === 0) {
          setSnackbarMessage('選択されたプランが見つかりません', { type: 'error' });
          return;
        }

        const result = await archivePlans(planIdentifiers, false);

        if (result.success.length > 0) {
          setSnackbarMessage(`${result.success.length}件のプランをアクティブにしました`, {
            type: 'success',
          });
          await refetch();
          setSelectedPlans([]);
        }

        if (result.notFound.length > 0) {
          setSnackbarMessage(`${result.notFound.length}件のプランが見つかりませんでした`, {
            type: 'error',
          });
        }
      } catch (error) {
        console.error('Error activating plans:', error);
        setSnackbarMessage('アクティブへの変更に失敗しました', { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [refetch, setIsLoading, setSnackbarMessage, getPlanIdentifiers]
  );

  return {
    // Data
    searchResults: plans,
    loading: isFetching,
    error: isError,

    // Pagination
    currentPage,
    itemsPerPage,
    totalItems,
    totalPages: Math.ceil(totalItems / itemsPerPage),

    // Selection
    selectedPlans,

    // Search state
    searchParams: searchInputValues, // Return input values for form fields
    committedSearchParams,

    // Sorting - Convert enum to string for table component
    sortField: sortField as string | null,
    sortOrder,

    // Handlers
    handleSearch,
    handleSearchChange,
    handleClear,
    handlePageChange,
    handleItemsPerPageChange,
    handleSelectionChange,
    handleSortChange,
    handleArchive,

    // Action handlers for filter buttons
    handleConfirmed,
    handleUnconfirmed,
    handleActive,

    // API actions
    refetch,
  };
};
